import React from 'react';
import cx from 'classnames';
import PauseButton from '../europe/PauseButton';
import DataSourcePicker from './DataSourcePicker';
import css from './BOControlPanel.module.scss';

// 顶部 BO 控制台：模式选择 / 局数 / Fearless / 先手切换 / 暂停 / 下一局 / 重置 / 导出战术板
const BOControlPanel = ({
  // BO state
  mode,
  totalGames,
  currentGameIndex,
  currentGameNumber,
  currentFirstSide,
  nextFirstSide,
  fearlessEnabled,
  isSeriesComplete,
  completedGames,
  viewingGameIndex,
  isReplayMode,
  replayEditing,          // 复盘编辑开关
  onToggleReplayEdit,
  onViewGame,             // (index) => void
  onReturnToCurrent,      // () => void
  onRollbackToGame,       // (index) => void  破坏性，UI 弹确认
  // Draft state
  draftStarted,
  draftFinished,
  // Timer
  isPaused,
  // Handlers
  onChangeMode,
  onToggleFearless,
  onToggleCurrentFirstSide,
  onToggleNextFirstSide,
  onTogglePause,
  onNextGame,
  onResetBO,
  // Export
  onExport,
  exporting,
  onOpenArchive,           // () => void  打开 BO 档案抽屉
  onSaveCurrentBO,         // () => void  保存当前 BO 为存档
  canSaveCurrentBO,        // bool 当前 BO 是否有可保存内容（至少打完一局）
  archiveCount,            // 当前已有多少存档（角标）
  // AI
  onOpenAI,
  aiOn,
  // 数据源（多选合并）
  dataSources,
  selectedSourceIds,
  onToggleSource,
  onSelectOnly,
  onSelectByLeague,
  currentMeta,
  dataLoading,
}) => {
  const canEditCurrentFirstSide = !draftStarted;
  const canAdvanceNextGame = draftFinished && !isSeriesComplete;

  return (
    <div className={css.Panel}>
      <div className={css.Brand}>
        <span className={css.BrandTitle}>COACH MODE</span>
        <span className={css.BrandSub}>教练手动推演</span>
      </div>

      <Divider />

      {/* BO 模式选择 */}
      <div className={css.Group}>
        <span className={css.GroupLabel}>赛制</span>
        <div className={css.SegmentedGroup}>
          {['BO1', 'BO3', 'BO5'].map((m) => (
            <button
              key={m}
              type="button"
              className={cx(css.SegBtn, mode === m && css.SegBtnActive)}
              onClick={() => onChangeMode(m)}
              title="切换赛制会重置整个系列"
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <Divider />

      {/* 局数胶囊 + 复盘切换：点击任意 Game 可只读复盘，长按右键可撤销到该局 */}
      <div className={css.Group}>
        <span className={css.GroupLabel}>局数</span>
        <div className={css.GameChips}>
          {Array.from({ length: totalGames }, (_, i) => {
            const completed = !!completedGames && completedGames.some((g) => g.gameIndex === i);
            const isCurrentLive = i === currentGameIndex && !isSeriesComplete;
            const isViewing = (viewingGameIndex == null && isCurrentLive)
                            || viewingGameIndex === i;
            const clickable = completed || isCurrentLive;
            return (
              <button
                key={i}
                type="button"
                className={cx(
                  css.GameChip,
                  completed && css.GameChipDone,
                  isCurrentLive && css.GameChipLive,
                  isViewing && css.GameChipViewing,
                  !clickable && css.GameChipFuture,
                )}
                disabled={!clickable}
                onClick={() => {
                  if (isCurrentLive) {
                    onReturnToCurrent && onReturnToCurrent();
                  } else if (completed) {
                    onViewGame && onViewGame(i);
                  }
                }}
                title={
                  isCurrentLive ? '当前正在打的局（点击回到现局）'
                  : completed   ? `Game ${i + 1}：复盘只读（点击查看 BP）`
                  :              `Game ${i + 1}：尚未开始`
                }
              >
                <span className={css.GameChipNum}>{i + 1}</span>
                {completed && <span className={css.GameChipDoneDot} aria-hidden="true" />}
                {isCurrentLive && <span className={css.GameChipLiveDot} aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        {/* 复盘状态时显示一组小按钮：编辑 / 返回当前局 / 撤销到此局 */}
        {isReplayMode && (
          <div className={css.ReplayActions}>
            <span className={cx(css.ReplayTag, replayEditing && css.ReplayTagEditing)}>
              {replayEditing ? '编辑中 · Game ' : '复盘 Game '}
              {viewingGameIndex + 1}
            </span>
            <button
              type="button"
              className={cx(css.ReplayEditBtn, replayEditing && css.ReplayEditBtnOn)}
              onClick={onToggleReplayEdit}
              title={replayEditing
                ? '关闭编辑：回到只读模式'
                : '启用编辑：可 swap 或点击 Pick/Ban slot 替换英雄。Fearless 锁会自动重算'}
            >
              <span className={css.ReplayEditIcon} aria-hidden="true">
                {replayEditing ? '✎' : '✎'}
              </span>
              {replayEditing ? '编辑中' : '启用编辑'}
            </button>
            <button
              type="button"
              className={css.ReplayBackBtn}
              onClick={onReturnToCurrent}
              title="退出复盘，回到当前正在打的局"
            >
              ⇦ 返回当前局
            </button>
            <button
              type="button"
              className={css.ReplayRollbackBtn}
              onClick={() => {
                const ok = window.confirm(
                  `确定要撤销到 Game ${viewingGameIndex + 1} 重新推演吗？\n\n` +
                  `Game ${viewingGameIndex + 1} 之后的所有 BP 数据会被清空，\n` +
                  `Fearless 全局锁也会回退到该局之前的状态。\n\n` +
                  `此操作不可恢复。`
                );
                if (ok) onRollbackToGame(viewingGameIndex);
              }}
              title="把 BO 状态彻底回退到此局开始之前（破坏性，会清掉后续 BP）"
            >
              ⌫ 撤销到此局
            </button>
          </div>
        )}

        {isSeriesComplete && !isReplayMode && <span className={css.DoneTag}>系列结束</span>}
      </div>

      <Divider />

      {/* Fearless 开关 */}
      <div className={css.Group}>
        <button
          type="button"
          className={cx(css.Toggle, fearlessEnabled && css.ToggleOn)}
          onClick={onToggleFearless}
          title="开启后：被任意一方 Pick 过的英雄，在后续所有局都会被永久置灰，双方不可再选"
        >
          <span className={css.ToggleDot} />
          <span className={css.ToggleLabel}>
            Fearless
            <span className={css.ToggleSub}>{fearlessEnabled ? '已开启' : '已关闭'}</span>
          </span>
        </button>
      </div>

      <Divider />

      {/* 当前局先手 + 下一局先手 */}
      <div className={css.Group}>
        <button
          type="button"
          className={cx(css.SideBtn, css[`Side_${currentFirstSide}`])}
          onClick={onToggleCurrentFirstSide}
          disabled={!canEditCurrentFirstSide}
          title={canEditCurrentFirstSide
            ? '点击切换本局先手（蓝/红）'
            : '本局已开始，无法切换；可以预约下一局先手'}
        >
          <span className={css.SideArrow}>⇄</span>
          <span className={css.SideText}>
            本局先手 <strong>{currentFirstSide === 'blue' ? '蓝' : '红'}</strong>
          </span>
        </button>

        <button
          type="button"
          className={cx(css.SideBtn, css[`Side_${nextFirstSide}`], css.NextSideBtn)}
          onClick={onToggleNextFirstSide}
          title="预约下一局先手"
        >
          <span className={css.SideArrow}>›</span>
          <span className={css.SideText}>
            下一局 <strong>{nextFirstSide === 'blue' ? '蓝' : '红'}</strong>
          </span>
        </button>
      </div>

      <Divider />

      {/* 暂停按钮 + 下一局 + 重置 */}
      <div className={cx(css.Group, css.GroupActions)}>
        <PauseButton
          isPaused={isPaused}
          onToggle={onTogglePause}
          disabled={draftFinished}
        />

        <button
          type="button"
          className={cx(css.NextGameBtn, canAdvanceNextGame && css.NextGameBtnReady)}
          onClick={onNextGame}
          disabled={!canAdvanceNextGame}
          title={canAdvanceNextGame ? '把本局选用的英雄写入 Fearless 全局锁，并开启下一局' : '本局完成后才能进入下一局'}
        >
          <span className={css.NextGameIcon}>⏭</span>
          <span>进入 Game {Math.min(currentGameNumber + 1, totalGames)}</span>
        </button>

        <button
          type="button"
          className={cx(css.ResetBOBtn)}
          onClick={onResetBO}
          title="清空所有局数据，回到 Game 1"
        >
          <span className={css.ResetIcon}>↺</span>
          <span>重置 BO</span>
        </button>

        {/* 一键导出战术板 = PNG 截图 + JSON 数据，赛后微信群分享用 */}
        <button
          type="button"
          data-export-ignore="true"
          className={cx(css.ExportBtn, exporting && css.ExportBtnRunning)}
          onClick={onExport}
          disabled={!onExport || exporting}
          title="导出 PNG 截图 + JSON 数据（含本局 BP、BO 进度、Fearless 锁池）"
        >
          <span className={css.ExportIcon} aria-hidden="true">
            {exporting ? (
              <svg viewBox="0 0 24 24" width="16" height="16">
                <circle
                  cx="12" cy="12" r="9"
                  fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeDasharray="14 42" strokeLinecap="round"
                >
                  <animateTransform attributeName="transform" type="rotate"
                    from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
                </circle>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
              </svg>
            )}
          </span>
          <span>{exporting ? '生成中…' : '导出战术板'}</span>
        </button>

        {/* 保存当前 BO 到档案 —— 只要打完至少一局就能保存 */}
        <button
          type="button"
          data-export-ignore="true"
          className={css.SaveBOBtn}
          onClick={onSaveCurrentBO}
          disabled={!canSaveCurrentBO}
          title={canSaveCurrentBO
            ? '把当前 BO 系列保存到本地档案（localStorage 永久保留）'
            : '至少打完一局才能保存'}
        >
          <span className={css.SaveBOIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
          </span>
          <span>保存 BO</span>
        </button>

        {/* 打开档案抽屉 —— 看历史 BO、导出系列总结 PNG */}
        <button
          type="button"
          data-export-ignore="true"
          className={css.ArchiveBtn}
          onClick={onOpenArchive}
          title="打开 BO 档案：查看所有历史 BO + 一键导出系列总结图"
        >
          <span className={css.ArchiveIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7l1.5-3h15L21 7" />
              <path d="M3 7h18v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7z" />
              <path d="M10 12h4" />
            </svg>
          </span>
          <span>档案</span>
          {typeof archiveCount === 'number' && archiveCount > 0 && (
            <span className={css.ArchiveCount}>{archiveCount}</span>
          )}
        </button>

        {/* 数据源多选合并：勾选多份会按英雄累加 picks/bans 并重算 PR/BR/WR */}
        {dataSources && dataSources.length > 0 && (
          <DataSourcePicker
            sources={dataSources}
            selectedSourceIds={selectedSourceIds || []}
            onToggle={onToggleSource}
            onSelectOnly={onSelectOnly}
            onSelectByLeague={onSelectByLeague}
            loading={dataLoading}
            currentMeta={currentMeta}
          />
        )}

        {/* AI 齿轮按钮：打开 AI 设置面板 */}
        <button
          type="button"
          data-export-ignore="true"
          className={cx(css.AIGearBtn, aiOn && css.AIGearBtnOn)}
          onClick={onOpenAI}
          title={aiOn ? 'AI 已开启 · 点击管理推荐功能' : 'AI 已关闭 · 点击启用'}
        >
          <span className={css.AIGearIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </span>
          <span>AI</span>
          <span className={cx(css.AIGearDot, aiOn ? css.AIGearDotOn : css.AIGearDotOff)} />
        </button>
      </div>
    </div>
  );
};

const Divider = () => <span className={css.Divider} />;

export default BOControlPanel;
